import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";
import { errorMessage, successMessage } from "../../../utils";
import {
  getPointPromotionDetailsApi,
  changePointPromotionStatusApi,
  deletePointPromotionApi,
} from "../api";

export const usePointPromotionDetails = ({ id, enableApi = true }) => {
  const navigate = useNavigate();
  const [state, setState] = useImmer({
    isLoading: true,
    isSaveButtonBusy: false,
    data: null,
    statusModalOpen: false,
    statusModalType: "",
    deleteModalOpen: false,
    httpStatusCodes: {
      details: "",
    },
  });

  const getPointPromotionDetails = async (detailsId) => {
    try {
      setState((draft) => {
        draft.isLoading = true;
      });

      const res = await getPointPromotionDetailsApi(detailsId);

      if (res?.success) {
        setState((draft) => {
          draft.data = res.data;
          draft.isLoading = false;
          draft.httpStatusCodes.details = "";
        });
      } else {
        setState((draft) => {
          draft.isLoading = false;
        });
        errorMessage(res?.message || "Unable to fetch point promotion details");
      }
    } catch (err) {
      setState((draft) => {
        draft.isLoading = false;
        draft.httpStatusCodes.details = err?.response?.status || "";
      });
      errorMessage(
        err?.response?.data?.message ||
          "Unable to fetch point promotion details",
      );
    }
  };

  useEffect(() => {
    if (enableApi && id) {
      getPointPromotionDetails(id);
    }
  }, [enableApi, id]);

  const handleStatusModal = (type, isOpen) => {
    setState((draft) => {
      draft.statusModalOpen = isOpen;
      draft.statusModalType = isOpen ? type : "";
    });
  };

  const handleChangeStatus = async (type) => {
    const isPublish = type === "publish";
    try {
      setState((draft) => {
        draft.isSaveButtonBusy = true;
      });

      const res = await changePointPromotionStatusApi(id);
      if (res?.success) {
        successMessage(
          res.message ||
            (isPublish ? "Published successfully" : "Paused successfully"),
        );
        setState((draft) => {
          draft.data.is_active = !Boolean(draft.data?.is_active);
          draft.statusModalOpen = false;
          draft.statusModalType = "";
          draft.isSaveButtonBusy = false;
        });
      } else {
        setState((draft) => {
          draft.isSaveButtonBusy = false;
        });
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message ||
          (isPublish
            ? "Failed to publish promotion"
            : "Failed to pause promotion"),
      );
      setState((draft) => {
        draft.isSaveButtonBusy = false;
      });
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!state.statusModalType) return;
    await handleChangeStatus(state.statusModalType);
    getPointPromotionDetails(id);
  };

  const handleDeleteModal = (isOpen) => {
    setState((draft) => {
      draft.deleteModalOpen = isOpen;
    });
  };

  const handleDeletePromotion = async () => {
    try {
      setState((draft) => {
        draft.isSaveButtonBusy = true;
      });

      const res = await deletePointPromotionApi(id);
      if (res?.success) {
        successMessage(res.message || "Point promotion deleted successfully");
        setState((draft) => {
          draft.isSaveButtonBusy = false;
          draft.deleteModalOpen = false;
        });
        navigate(-1);
      } else {
        setState((draft) => {
          draft.isSaveButtonBusy = false;
        });
      }
    } catch (err) {
      errorMessage(
        err?.response?.data?.message || "Failed to delete point promotion",
      );
      setState((draft) => {
        draft.isSaveButtonBusy = false;
      });
    }
  };

  return {
    state,
    handleStatusModal,
    handleConfirmStatusChange,
    handleDeleteModal,
    handleDeletePromotion,
  };
};
